import { useState, useEffect, useRef } from "react";

import { Helmet } from "react-helmet";


const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    ::selection { background: #bfdbfe; color: #1e3a5f; }

    body { background: #fff; }

    .font-lora { font-family: 'Lora', Georgia, serif; }
    .font-dm   { font-family: 'DM Sans', system-ui, sans-serif; }

    /* Article body prose */
    .blog-prose {
      font-family: 'Lora', Georgia, serif;
      font-size: 1.125rem;
      line-height: 1.85;
      color: #1f2937;
    }
    .blog-prose p {
      margin-bottom: 1.6em;
    }
    .blog-prose strong {
      font-weight: 600;
      color: #111827;
    }
    .blog-prose a {
      color: #2563eb;
      text-decoration: underline;
      text-underline-offset: 3px;
    }
    .blog-prose a:hover {
      color: #1d4ed8;
    }

    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .fade-in-up { animation: fadeInUp 0.6s ease both; }
    .delay-1 { animation-delay: 0.1s; }
    .delay-2 { animation-delay: 0.2s; }
    .delay-3 { animation-delay: 0.3s; }
    .delay-4 { animation-delay: 0.4s; }

    @keyframes progressGrow {
      from { transform: scaleX(0); }
      to   { transform: scaleX(1); }
    }
  `}</style>
);

function useScrollReveal() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
    const [ref, visible] = useScrollReveal();
    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(18px)",
                transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
            }}
        >
            {children}
        </div>
    );
}

// Inline image placeholder
function BlogImage({ src, alt = "", caption, aspectRatio = "16/9" }) {
    return (
        <FadeIn className="my-10">
            <figure>
                <div
                    className="w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-100"
                    style={{ aspectRatio }}
                >
                    <img
                        src={src}
                        alt={alt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                </div>

                {caption && (
                    <figcaption className="font-dm text-sm text-slate-400 text-center mt-3 leading-relaxed px-4">
                        {caption}
                    </figcaption>
                )}
            </figure>
        </FadeIn>
    );
}

// Blockquote
function Blockquote({ children }) {
    const [ref, visible] = useScrollReveal();
    return (
        <blockquote
            ref={ref}
            className="my-10 border-l-[3px] border-blue-500 pl-6 pr-2"
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateX(0)" : "translateX(-12px)",
                transition: "opacity 0.6s ease, transform 0.6s ease",
            }}
        >
            <p className="font-lora italic text-xl leading-relaxed text-slate-600">
                {children}
            </p>
        </blockquote>
    );
}

// Small callout box
function Callout({ label, children }) {
    return (
        <FadeIn className="my-10">
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-6 py-5">
                <p className="font-dm text-xs font-semibold tracking-widest uppercase text-blue-500 mb-2">{label}</p>
                <p className="font-lora text-base text-slate-700 leading-relaxed">{children}</p>
            </div>
        </FadeIn>
    );
}

// Tags
function Tag({ label }) {
    return (
        <span className="font-dm inline-block text-xs font-medium tracking-wide px-3 py-1 rounded-full bg-slate-100 text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors cursor-default mr-2 mb-2">
      {label}
    </span>
    );
}

export default function KQAICADMoUBlogPost() {
    const [readProgress, setReadProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const doc = document.documentElement;
            const scrolled = doc.scrollTop;
            const total = doc.scrollHeight - doc.clientHeight;
            setReadProgress(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const tags = ["Aviation", "AI & Machine Learning", "Africa", "Digital Skills", "Innovation", "Partnership"];

    return (
        <>
            <Helmet>
                <title>
                    AICEX-Africa to Implement the AICAD & Kenya Airways Partnership
                </title>

                <meta
                    name="description"
                    content="AICAD and Kenya Airways sign a strategic MoU to advance AI, digital skills, applied research, and innovation across Africa through AICEX-Africa."
                />

                {/* Open Graph (Facebook, LinkedIn, WhatsApp) */}
                <meta property="og:type" content="article"/>
                <meta
                    property="og:title"
                    content="AICEX-Africa to Implement the AICAD & Kenya Airways Partnership"
                />
                <meta
                    property="og:description"
                    content="A strategic partnership between AICAD and Kenya Airways to strengthen AI, digital skills, and innovation across Africa."
                />
                <meta
                    property="og:image"
                    content="https://aospea.org/images/MD_and-CEO-shake-hands-and-displaying-signed-MOU.PNG"
                />
                <meta
                    property="og:url"
                    content="https://aospea.org/news/kenya-airways-aicad-mou-digital-skills-innovation"
                />
                <meta property="og:site_name" content="AICEX-Africa"/>

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image"/>
                <meta
                    name="twitter:title"
                    content="AICEX-Africa to Implement the AICAD & Kenya Airways Partnership"
                />
                <meta
                    name="twitter:description"
                    content="AICAD and Kenya Airways partner to advance AI and digital skills development across Africa."
                />
                <meta
                    name="twitter:image"
                    content="https://aospea.org/images/MD_and-CEO-shake-hands-and-displaying-signed-MOU.PNG"
                />
            </Helmet>

            <div className="bg-white min-h-screen font-dm" style={{fontFamily: "'DM Sans', sans-serif"}}>
                <GlobalStyles/>

                {/* Reading progress bar */}
                <div className="fixed top-0 left-0 right-0 z-50 h-[3px] bg-slate-100">
                    <div
                        className="h-full bg-blue-500 origin-left transition-all duration-150"
                        style={{transform: `scaleX(${readProgress / 100})`}}
                    />
                </div>

                {/* ── HERO / Cover Image ── */}
                <div className="w-full bg-slate-900 relative overflow-hidden
                h-[55vw]
                max-h-[820px]
                min-h-[420px]">

                    {/* Subtle texture */}
                    <div className="absolute inset-0" style={{
                        backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
                        backgroundSize: "28px 28px",
                    }}/>
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"/>

                    {/* Cover photo */}
                    <div className="absolute inset-0">
                        <img
                            src="/images/group-photo.png" // update path as needed
                            alt="AICAD and Kenya Airways officials during MoU signing ceremony"
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"/>


                    {/* Category badge */}
                    <div className="absolute top-8 left-8">
          <span
              className="fade-in font-dm text-xs font-semibold tracking-widest uppercase bg-blue-500 text-white px-3 py-1.5 rounded-full">
            Partnership News
          </span>
                    </div>

                    {/* Hero title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 px-8 pb-10 max-w-3xl">
                        <h1 className="fade-in delay-1 font-lora text-white leading-tight mb-3"
                            style={{fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700}}>
                            AICEX-Africa to Implement the AICAD &amp; Kenya Airways Partnership
                        </h1>
                    </div>
                </div>

                {/* ── ARTICLE WRAPPER ── */}
                <div className="max-w-4xl lg:max-w-6xl mx-auto px-5 sm:px-6">

                    {/* Author / meta row */}
                    <div
                        className="fade-in delay-2 flex items-center justify-between flex-wrap gap-4 py-6 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                            {/* Avatar */}
                            <div
                                className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
                                <span className="font-dm font-semibold text-white text-sm">A</span>
                            </div>
                            <div>
                                <p className="font-dm font-semibold text-slate-800 text-sm leading-tight">AICEX-Africa</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-400 font-dm">
                            <span>10 February 2026</span>
                            <span className="w-1 h-1 rounded-full bg-slate-300"/>
                            <span>5 min read</span>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="fade-in delay-3 pt-5 pb-2">
                        {tags.map(t => <Tag key={t} label={t}/>)}
                    </div>

                    {/* ── ARTICLE BODY ── */}
                    <article className="pt-8 pb-24">

                        {/* Lead paragraph — slightly larger */}
                        <FadeIn>
                            <p className="font-lora text-xl leading-relaxed text-slate-700 mb-8 font-medium">
                                On 10th February 2026, the African Institute for Capacity Development (AICAD)
                                signed a Memorandum of Understanding with Kenya Airways PLC (KQ) — marking a
                                significant step forward in advancing digital skills, applied research, and
                                innovation across the continent.
                            </p>
                        </FadeIn>

                        {/* Photo 1 */}
                        <BlogImage
                            src="/images/MD_and-CEO-shake-hands-and-displaying-signed-MOU.PNG"
                            alt="AICAD and Kenya Airways officials during MoU signing"
                            caption="Ag. MD and CEO, Capt. George Kamal of KQ and Prof. James Njiru displaying the signed MoU."
                        />


                        <FadeIn>
                            <div className="blog-prose">
                                <p>
                                    The MoU was signed by <strong>Prof. James Njiru</strong>, Executive Director of
                                    AICAD, and
                                    <strong> Ag. MD and CEO, Capt. George Kamal</strong> of KQ. Also present at the
                                    signing were
                                    Fred Kitunga, Chief Information and Data Officer of KQ, and Prof. Muliaro
                                    Wafula, Lead Scientist of the Artificial Intelligence Centre of Excellence for
                                    Capacity Building in Africa (AICEX-Africa —{" "}
                                    <a href="https://aospea.org/ai-coe" target="_blank"
                                       rel="noopener noreferrer">aospea.org/ai-coe</a>).
                                </p>
                                <p>
                                    This partnership is designed to strengthen capabilities in Software
                                    Engineering, Data &amp; Analytics, Cybersecurity, and Artificial Intelligence,
                                    among other emerging technologies — while creating opportunities for
                                    training, internships, mentorship, and real-world project experience.
                                </p>
                            </div>
                        </FadeIn>

                        <Blockquote>
                            "Through this initiative, AICAD and KQ are investing in talent, innovation, and
                            a future-ready digital workforce for the region."
                        </Blockquote>

                        {/* Section heading */}
                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5"
                                style={{fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)"}}>
                                Why Kenya Airways, Why Now?
                            </h2>
                            <div className="blog-prose">
                                <p>
                                    Kenya Airways operates in a highly dynamic global aviation environment
                                    shaped by rapid technological change, evolving regulatory frameworks, and a
                                    growing need for efficiency and workforce transformation. As one of Africa's
                                    leading carriers, KQ recognises that sustainable competitiveness increasingly
                                    depends on deep digital capability — not just at the leadership level, but
                                    throughout the organisation.
                                </p>
                                <p>
                                    AICAD, in collaboration with Jomo Kenyatta University of Agriculture and
                                    Technology (JKUAT), hosts AICEX-Africa, currently among the leading centres
                                    on the continent in applied research and innovation. This positions AICEX-Africa
                                    as uniquely suited to complement KQ's strategic ambitions with practical,
                                    context-grounded solutions.
                                </p>
                            </div>
                        </FadeIn>

                        {/* Section heading */}
                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5"
                                style={{fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)"}}>
                                What the Partnership Covers
                            </h2>
                            <div className="blog-prose">
                                <p>
                                    Through this MoU, AICAD and KQ have committed to joint human capital
                                    development — co-designing and delivering customised training aligned to Kenya
                                    Airways' strategic and operational needs. This includes undertaking applied
                                    research and innovation to address challenges.
                                </p>
                            </div>
                        </FadeIn>

                        <FadeIn>
                            <div className="blog-prose">
                                <p>
                                    Under this partnership, <strong>AICEX-Africa will serve as the primary
                                    implementation partner</strong> — supporting Kenya Airways in accessing
                                    regionally grounded expertise in AI, developing talent pipelines, and
                                    delivering applied innovation capabilities.
                                </p>
                            </div>
                        </FadeIn>

                        {/* Section heading */}
                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5"
                                style={{fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)"}}>
                                The Role of AICEX-Africa
                            </h2>
                            <div className="blog-prose">
                                <p>
                                    AICEX Africa was established to function as a pan-African platform for AI
                                    capacity building, bringing together policy, research, industry, and talent
                                    ecosystems. Through AICAD's extensive footprint across African institutions
                                    and innovation networks, AICEX Africa enables organisations to tap into
                                    distributed expertise, context-aware solutions, and scalable AI training models.
                                </p>
                            </div>
                        </FadeIn>

                        <Callout label="Core Implementation Principle">
                            A guiding principle of AICEX Africa's approach is an <strong>open-source-first
                            philosophy</strong> —
                            prioritising open technologies while ensuring production-grade deployment, robustness,
                            security,
                            and scalability. This supports sustainability, skills transfer, and local ownership while
                            meeting enterprise and industry requirements.
                        </Callout>

                        {/* Photo 2 */}
                        <BlogImage
                            src="/images/group-photo.png"
                            alt="AICAD and Kenya Airways officials during MoU signing"

                            caption="Group photo of AICAD and KQ officials who attended the MoU signing ceremony."
                        />

                        {/* Final section */}
                        <FadeIn>
                            <h2 className="font-lora font-bold text-slate-900 mt-12 mb-5"
                                style={{fontSize: "clamp(1.35rem, 2.5vw, 1.65rem)"}}>
                                A Signal for the Continent
                            </h2>
                            <div className="blog-prose">
                                <p>
                                    The partnership reflects a shared commitment to building African capability
                                    for African and global competitiveness — particularly in sectors where
                                    operational excellence, innovation, and technology leadership are critical.
                                </p>
                                <p>
                                    More broadly, it signals a shift toward <strong>African-led, locally developed
                                    solutions</strong> — where open, scalable technologies and continental expertise
                                    are mobilised to support industry transformation, skills development, and
                                    long-term innovation leadership. As AICEX Africa continues to expand its reach,
                                    partnerships like this one with Kenya Airways demonstrate what becomes possible
                                    when Africa invests in its own capacity.
                                </p>
                            </div>
                        </FadeIn>


                        {/* Share / footer row */}
                        <FadeIn>
                            <div
                                className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
                                <div className="flex flex-wrap gap-2">
                                    {tags.slice(0, 4).map(t => <Tag key={t} label={t}/>)}
                                </div>
                                <p className="font-dm text-xs text-slate-300">
                                    Published 10 Feb 2026
                                </p>
                            </div>
                        </FadeIn>

                    </article>
                </div>
            </div>

        </>

    );
}